import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  ModalFooter,
  Radio,
  RadioGroup,
  Text,
} from "@chakra-ui/react";
import CustomApprovalModal from "@/shared/components/custom-modal";
import CustomNoResults from "@/shared/components/no-results/CustomNoResults";
import CenteredSpinner from "@/shared/components/centered-spinner";
import StatusBadge from "@/shared/components/StatusBadge";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useCompanyIdentity from "@/shared/hooks/useCompanyIdentity";
import useGetShippingInfos from "@/shared/hooks/shipping-infos/useGetShippingInfos";
import usePostShippingInfo from "@/shared/hooks/shipping-infos/usePostShippingInfo";
import { ShippingInfoResponse } from "@/shared/api/shipping-infos/types";
import ShippingInfoFormModal, {
  ShippingInfoFormValues,
} from "@/shared/components/shipping-info/ShippingInfoFormModal";

interface Props {
  isOpen: boolean;
  // 취소 / 헤더 X — 다음 단계로 넘어가지 않고 이전 상태로 돌아간다는 뜻이다.
  onClose: () => void;
  // 확정 — 고른 배송지 객체 전체를 넘긴다(id만 주면 호출부가 다시 찾아야 한다).
  // 모달을 닫는 책임도 호출부에 있다.
  onSubmit: (shippingInfo: ShippingInfoResponse) => void;
  title?: string;
  description?: string;
  cancelName?: string;
  confirmName?: string;
  // 호출부가 실행 중인 mutation의 진행 상태 — 확정 버튼 로딩에 쓴다.
  isConfirmLoading?: boolean;
  // 배송지 목록 아래에 끼워 넣는 추가 입력 영역(예: 패스픽 주문의 입금자명).
  // 이 모달이 주문 직전 마지막 단계라, 별도 단계를 늘리는 대신 여기서 함께 받는다.
  extraContent?: React.ReactNode;
  // 추가 입력이 유효하지 않을 때 호출부가 확정 버튼을 잠근다(배송지 미선택과 OR).
  isConfirmDisabled?: boolean;
  // 미지정 시 useCompanyIdentity().companyId를 바이어 id로 쓴다.
  buyerId?: number;
  // 지정 시 우선 선택. 없으면 기본 배송지 → 첫 항목 순으로 고른다.
  initialSelectedId?: number | null;
}

function ShippingInfoSelectModal({
  isOpen,
  onClose,
  onSubmit,
  title,
  description,
  cancelName,
  confirmName,
  isConfirmLoading,
  extraContent,
  isConfirmDisabled,
  buyerId,
  initialSelectedId,
}: Props) {
  const { t } = useI18nHelper();
  const { companyId } = useCompanyIdentity();
  const targetBuyerId = buyerId ?? companyId;

  const { shippingInfos, isPending } = useGetShippingInfos(targetBuyerId);
  const { mutateAsync: postShippingInfo, isPending: isPosting } =
    usePostShippingInfo(targetBuyerId);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  // 배송지 추가는 대상 바이어 id가 있어야 경로를 만들 수 있다. 권한은 백엔드가 검증한다.
  const canRegister = Boolean(targetBuyerId);
  // 바이어 id가 없으면 쿼리가 enabled:false라 isPending이 계속 true다 —
  // 영원한 스피너 대신 결과 없음 화면을 보여준다.
  const isLoading = isPending && canRegister;
  const isEmpty = !isLoading && (shippingInfos?.length ?? 0) === 0;

  useEffect(() => {
    if (!isOpen) {
      setSelectedId(null);
      setIsFormOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !shippingInfos) {
      return;
    }
    // 이미 유효한 선택이 있으면 유지한다 — 방금 추가한 배송지가 목록 갱신에 덮이지 않게 한다.
    if (shippingInfos.some((info) => info.id === selectedId)) {
      return;
    }
    const preferred =
      initialSelectedId != null
        ? shippingInfos.find((info) => info.id === initialSelectedId)
        : undefined;
    const fallback =
      preferred ??
      shippingInfos.find((info) => info.isDefault) ??
      shippingInfos[0];
    setSelectedId(fallback?.id ?? null);
  }, [isOpen, shippingInfos, initialSelectedId, selectedId]);

  const handleCreate = async (values: ShippingInfoFormValues) => {
    // 빈 문자열로 null을 덮지 않도록 값이 없으면 필드를 빼고 보낸다.
    const created = await postShippingInfo({
      alias: values.alias,
      recipientName: values.recipientName,
      address: values.address,
      zipCode: values.zipCode,
      contact: values.contact || undefined,
      detailAddress: values.detailAddress || undefined,
      isDefault: values.isDefault,
    });
    setSelectedId(created.id);
    setIsFormOpen(false);
  };

  const handleConfirm = () => {
    const selected = shippingInfos?.find((info) => info.id === selectedId);
    if (!selected) {
      return;
    }
    onSubmit(selected);
  };

  return (
    <>
      <CustomApprovalModal
        isOpen={isOpen}
        onClose={onClose}
        // 화면보다 넓으면 왼쪽이 스크롤로도 닿지 않아 화면 폭으로 클램프한다.
        minWidth={"min(480px, 92dvw)"}
        maxWidth={"min(480px, 92dvw)"}
        isCentered={true}
        scrollBehavior={"inside"}
        // 오클릭으로 진행 중인 플로우가 조용히 끊기면 안 된다.
        closeOnOverlayClick={false}
      >
        <CustomApprovalModal.Header
          title={title ?? t("common:shipping_info.select_modal.title")}
          onClose={onClose}
        />

        <CustomApprovalModal.Body>
          <Flex flexDirection={"column"} gap={"12px"}>
            <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
              {description ?? t("common:shipping_info.select_modal.description")}
            </Text>

            {/* 데이터가 도착할 때 모달 높이가 튀지 않게 최소 높이를 준다. */}
            {isLoading && <CenteredSpinner minHeight={"200px"} />}

            {isEmpty && (
              <Flex flexDirection={"column"} gap={"12px"}>
                <CustomNoResults
                  title={t("common:shipping_info.select_modal.empty_title")}
                  description={t(
                    "common:shipping_info.select_modal.empty_description"
                  )}
                />
                {canRegister && (
                  <AppButton
                    variant={"primary"}
                    width={"100%"}
                    onClick={() => setIsFormOpen(true)}
                  >
                    <Text textStyle={"body-sm-bold"}>
                      {t("common:shipping_info.select_modal.add")}
                    </Text>
                  </AppButton>
                )}
              </Flex>
            )}

            {shippingInfos && shippingInfos.length > 0 && (
              <>
                <RadioGroup
                  value={selectedId !== null ? String(selectedId) : ""}
                  onChange={(value) => setSelectedId(Number(value))}
                >
                  <Flex flexDirection={"column"} gap={"8px"}>
                    {shippingInfos.map((info) => {
                      const isSelected = selectedId === info.id;

                      return (
                        <Box
                          key={info.id}
                          padding={"12px"}
                          borderRadius={"8px"}
                          border={"0.5px solid"}
                          borderColor={isSelected ? "#1272EF" : "#D4D4D1"}
                          background={isSelected ? "#F0F6FF" : "#FCFCFB"}
                          cursor={"pointer"}
                          onClick={() => setSelectedId(info.id)}
                        >
                          <Radio
                            value={String(info.id)}
                            alignItems={"flex-start"}
                          >
                            <Flex flexDirection={"column"} gap={"4px"}>
                              <Flex gap={"6px"} alignItems={"center"}>
                                <Text
                                  textStyle={"body-sm-bold"}
                                  color={"#1D1D1B"}
                                >
                                  {info.alias}
                                </Text>
                                {info.isDefault && (
                                  <StatusBadge
                                    status={t(
                                      "common:shipping_info.select_modal.default_badge"
                                    )}
                                    colorVariant={"blue"}
                                    size={"small"}
                                  />
                                )}
                              </Flex>
                              <Text
                                textStyle={"body-sm-medium"}
                                color={"#444440"}
                              >
                                {`${info.recipientName}${
                                  info.contact ? ` · ${info.contact}` : ""
                                }`}
                              </Text>
                              <Text
                                textStyle={"body-sm-medium"}
                                color={"#6F6F6C"}
                              >
                                {`(${info.zipCode}) ${info.address}${
                                  info.detailAddress
                                    ? ` ${info.detailAddress}`
                                    : ""
                                }`}
                              </Text>
                            </Flex>
                          </Radio>
                        </Box>
                      );
                    })}
                  </Flex>
                </RadioGroup>

                {canRegister && (
                  <AppButton
                    variant={"white"}
                    width={"100%"}
                    onClick={() => setIsFormOpen(true)}
                  >
                    <Text textStyle={"body-sm-bold"}>
                      {t("common:shipping_info.select_modal.add")}
                    </Text>
                  </AppButton>
                )}
              </>
            )}
          </Flex>
          {extraContent}
        </CustomApprovalModal.Body>

        {/* 공용 Footer는 버튼 폭이 96px 고정이라 긴 확정 라벨이 들어가지 않아 직접 구성한다. */}
        <ModalFooter padding={"12px 16px"} gap={"8px"}>
          <AppButton
            variant={"white"}
            isDisabled={isConfirmLoading}
            onClick={onClose}
          >
            <Text textStyle={"body-sm-bold"}>
              {cancelName ?? t("common:cancel")}
            </Text>
          </AppButton>
          <AppButton
            variant={"primary"}
            isDisabled={selectedId === null || isConfirmDisabled}
            isLoading={isConfirmLoading}
            onClick={handleConfirm}
          >
            <Text textStyle={"body-sm-bold"}>
              {confirmName ?? t("common:shipping_info.select_modal.confirm")}
            </Text>
          </AppButton>
        </ModalFooter>
      </CustomApprovalModal>

      {isFormOpen && (
        <ShippingInfoFormModal
          isOpen={true}
          onClose={() => setIsFormOpen(false)}
          onSubmit={handleCreate}
          isSubmitting={isPosting}
        />
      )}
    </>
  );
}

export default ShippingInfoSelectModal;
